<template>
  <TransactionListTemplate
    :screenConfig="screenConfig"
    :backButtonRoute="'/settings/user'"
    :setLoading="setLoading"
    :persistenceKey="'role-permission-list'"
    :backButtonEnable="true"
  >
    <template #popup>
      <DxPopup
        :visible.sync="popup.visible"
        title="New Role"
        :width="440"
        :height="'auto'"
        :drag-enabled="true"
        :show-close-button="true"
        :hide-on-outside-click="false"
        :wrapperAttr="{ class: 'css-v2 gc-popup' }"
      >
        <div class="rpl-popup-form">
          <div class="rpl-popup-field">
            <label class="rpl-popup-label"
              >Role Name <span class="rpl-required">*</span></label
            >
            <DxTextBox
              v-model="popup.roleName"
              styling-mode="outlined"
              placeholder="Enter role name"
            />
          </div>
          <div class="rpl-popup-field">
            <label class="rpl-popup-label">Description</label>
            <DxTextBox
              v-model="popup.description"
              styling-mode="outlined"
              placeholder="Optional description"
            />
          </div>
          <div class="rpl-popup-field">
            <label class="rpl-popup-label"
              >Role Type <span class="rpl-required">*</span></label
            >
            <DxSelectBox
              v-if="isInternalAdmin"
              v-model="popup.roleType"
              :items="roleTypeItems"
              display-expr="label"
              value-expr="value"
              styling-mode="outlined"
              placeholder="Select role type"
            />
            <DxTextBox
              v-else
              value="Custom"
              styling-mode="outlined"
              :read-only="true"
            />
          </div>
          <div class="rpl-popup-field">
            <label class="rpl-popup-label"
              >License Type <span class="rpl-required">*</span></label
            >
            <DxSelectBox
              v-model="popup.licenseType"
              :items="licenseTypeItems"
              display-expr="label"
              value-expr="value"
              styling-mode="outlined"
              placeholder="Select license type"
            />
          </div>
          <div class="rpl-popup-actions">
            <DxButton
              text="Cancel"
              styling-mode="outlined"
              @click="popup.visible = false"
            />
            <DxButton
              text="Create & Edit"
              type="default"
              styling-mode="contained"
              @click="createRole"
            />
          </div>
        </div>
      </DxPopup>
    </template>
  </TransactionListTemplate>
</template>

<script>
import TransactionListTemplate from "../../../templates/transaction-list";
import RouteMixin from "@/_mixins/route.mixin";
import FormMixin from "@/_mixins/form.mixin";
import { RolePermissionService } from "../../../_services/api/settings/graycorp/role-permission.service";
import { FeatureStorage } from "../../../_services/storage";

export default {
  mixins: [RouteMixin, FormMixin],
  components: { TransactionListTemplate },
  data() {
    return {
      validationSummary: [],
      setLoading: false,
      roleTypeItems: [],
      licenseTypeItems: [],
      popup: {
        visible: false,
        roleName: "",
        description: "",
        roleType: "Custom",
        licenseType: null,
      },
      screenConfig: {
        title: "Role Permissions",
        pageRoute: "/role-permission",
        exportFileName: "Role Permissions",
        gridData: {
          gridOptions: {
            dataSource: {
              endpoint: "Role",
              filter: "",
              serverPaging: true,
              allowGrouping: false,
            },
            rowSelect: {
              view: this,
              page: "role-permission",
              mode: "review",
              idProperty: "RoleId",
            },
          },
          toolbar: {
            search: true,
            columnChooser: true,
            export: true,
            openFilter: true,
            expand: false,
            customButtons: [
              {
                icon: "plus",
                label: "Add New",
                clickAction: () => this.openCreateRole(),
                buttonLocation: "left",
              },
            ],
          },
          columns: [
            {
              screenField: "RoleName",
              dataField: "RoleName",
              gridDataField: "RoleName",
              label: "Role Name",
              allowHiding: false,
              sortOrder: "asc",
              sortIndex: 0,
            },
            {
              dataField: "Description",
              gridDataField: "Description",
              label: "Description",
            },
            {
              screenField: "RoleType",
              dataField: "RoleType",
              gridDataField: "RoleType",
              filterDataField: "RoleType",
              label: "Role Type",
              width: 130,
              filterIndex: 2,
            },
            {
              screenField: "LicenseType",
              dataField: "LicenseType",
              gridDataField: "LicenseType",
              filterDataField: "LicenseType",
              label: "License Type",
              width: 140,
              filterIndex: 3,
            },
            {
              screenField: "Status",
              filterIndex: 0,
            },
          ],
        },
      },
    };
  },
  computed: {
    isInternalAdmin() {
      return FeatureStorage.getUserType() === "Internal Administrator";
    },
  },
  methods: {
    openCreateRole() {
      this.popup = {
        visible: true,
        roleName: "",
        description: "",
        roleType: "Custom",
        licenseType: null,
      };
    },

    createRole() {
      if (!this.popup.roleName.trim()) {
        this.$notify("Role name is required", "error");
        return;
      }
      if (!this.popup.licenseType) {
        this.$notify("License type is required", "error");
        return;
      }
      this.showLoading(true);
      RolePermissionService.createRole({
        RoleName: this.popup.roleName.trim(),
        Description: this.popup.description.trim() || null,
        RoleType: this.isInternalAdmin ? this.popup.roleType : "Custom",
        LicenseType: this.popup.licenseType,
      })
        .then((response) => {
          this.popup.visible = false;
          const newId = response.data.RoleId;
          this.$router.push({ path: `/role-permission/edit/${newId}` });
        })
        .catch((error) => {
          this.displayServerError(error, "Failed to create role");
        })
        .finally(() => {
          this.showLoading(false);
        });
    },
  },
  created() {
    Promise.all([
      RolePermissionService.getRoleTypes(),
      RolePermissionService.getLicenseTypes(),
    ])
      .then(([roleTypesResp, licenseTypesResp]) => {
        this.roleTypeItems = (roleTypesResp.data.value || []).map((t) => ({
          label: t.DisplayName,
          value: t.Code,
        }));
        this.licenseTypeItems = (licenseTypesResp.data.value || [])
          .filter((t) => t.Inactive !== "Y")
          .map((t) => ({ label: t.DisplayName, value: t.Code }));
      })
      .catch((error) => {
        this.displayServerError(error, "Failed to load reference data");
      });
  },
};
</script>

<style scoped lang="scss">
.rpl-popup-form {
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  background: #fff;
}

.rpl-popup-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.rpl-popup-label {
  font-size: 12px;
  color: #555;
}

.rpl-required {
  color: #d32f2f;
}

.rpl-popup-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding-top: 4px;
}
</style>
